import { Hole, Rectangle, CalibrationConfig } from '../types';

/**
 * Reference points for geometric calibration
 * User clicks these 3 points to establish the playfield geometry
 */
export interface ReferencePoints {
  center30pt: { x: number; y: number };
  left100pt: { x: number; y: number };
  right100pt: { x: number; y: number };
}

/**
 * Calculate all zones from 3 reference points using known skee-ball geometry
 * 
 * Standard skee-ball layout (from top to bottom):
 * - 100pt holes (×2): Left and right at top
 * - 50pt hole: Center, below 100pt holes
 * - 40pt hole: Center, below 50pt
 * - 30pt hole: Center (reference point)
 * - 20pt ring: Circular zone around 30pt/40pt holes
 * - 10pt zone: Large outer zone containing everything
 * - Gutter: Below scoring zones
 * - Start: Ball entry zone at bottom
 * 
 * @param refPoints - The 3 reference points clicked by user
 * @returns Complete calibration configuration
 */
export function calculateZonesFromReferencePoints(refPoints: ReferencePoints): CalibrationConfig {
  const { center30pt, left100pt, right100pt } = refPoints;

  // Calculate key measurements from reference points
  const width100ptHoles = Math.abs(right100pt.x - left100pt.x);
  const centerX = (left100pt.x + right100pt.x) / 2;

  // Use the actual clicked Y positions for 100pt holes (user clicked centers)
  const y100pt = (left100pt.y + right100pt.y) / 2;

  // Hole radii (ratios based on calibration data analysis)
  const radius100pt = width100ptHoles * 0.08;
  const radius50pt = width100ptHoles * 0.13;
  const radius40pt = width100ptHoles * 0.13;
  const radius30pt = width100ptHoles * 0.18;
  const radius20pt = width100ptHoles * 0.44;
  const radius10pt = width100ptHoles * 0.73;

  // Calculate hole positions (Y coordinates relative to 100pt holes)
  const y50pt = y100pt + (width100ptHoles * 0.102);
  const y40pt = y100pt + (width100ptHoles * 0.389);
  const y30pt = center30pt.y; // Use actual clicked position

  const holes: Hole[] = [];

  // 100pt holes (×2) - Top left and right
  holes.push({
    id: '100pt-left',
    x: left100pt.x,
    y: y100pt,
    radius: radius100pt,
    value: 100,
    shape: 'circle',
  });
  holes.push({
    id: '100pt-right',
    x: right100pt.x,
    y: y100pt,
    radius: radius100pt,
    value: 100,
    shape: 'circle',
  });

  // 50pt hole - Center
  holes.push({
    id: '50pt',
    x: centerX,
    y: y50pt,
    radius: radius50pt,
    value: 50,
    shape: 'circle',
  });

  // 40pt hole - Center
  holes.push({
    id: '40pt',
    x: centerX,
    y: y40pt,
    radius: radius40pt,
    value: 40,
    shape: 'circle',
  });

  // 30pt hole - Center (reference point) - Same size as OLD 20pt
  holes.push({
    id: '30pt',
    x: center30pt.x,
    y: center30pt.y,
    radius: radius30pt,
    value: 30,
    shape: 'circle',
  });

  // 20pt ring - Centered on 30pt
  holes.push({
    id: '20pt',
    x: center30pt.x,
    y: center30pt.y,
    radius: radius20pt,
    value: 20,
    shape: 'circle',
  });

  // 10pt zone - Large outer zone centered on 30pt
  const y10pt = center30pt.y;
  holes.push({
    id: '10pt',
    x: center30pt.x,
    y: y10pt,
    radius: radius10pt,
    value: 10,
    shape: 'circle',
  });

  // Gutter zone - Rectangle below scoring area
  // Position based on calibration data analysis
  const gutterY = center30pt.y + radius10pt + (width100ptHoles * 0.1);
  const gutterHeight = width100ptHoles * 0.336;
  const gutterZone: Rectangle = {
    x: centerX - (width100ptHoles * 0.75),
    y: gutterY,
    width: width100ptHoles * 1.50,
    height: gutterHeight,
  };

  // Start zone - Rectangle at bottom (ball entry)
  const startY = gutterY + gutterHeight + (width100ptHoles * 0.05);
  const startHeight = width100ptHoles * 0.668;
  const startZone: Rectangle = {
    x: centerX - (width100ptHoles * 0.51),
    y: startY,
    width: width100ptHoles * 1.02,
    height: startHeight,
  };

  return {
    holes,
    startZone,
    gutterZone,
    timestamp: Date.now(),
  };
}

/**
 * Validate that reference points form a reasonable geometry
 * Returns true if points are valid, false otherwise
 */
export function validateReferencePoints(refPoints: Partial<ReferencePoints>): boolean {
  const { center30pt, left100pt, right100pt } = refPoints;

  // Check all points are defined
  if (!center30pt || !left100pt || !right100pt) {
    return false;
  }

  // Check that 100pt holes are horizontally separated
  const horizontalDistance = Math.abs(right100pt.x - left100pt.x);
  if (horizontalDistance < 50) {
    return false; // Too close together
  }

  // Check that 100pt holes are roughly at same height
  const verticalDifference = Math.abs(right100pt.y - left100pt.y);
  if (verticalDifference > horizontalDistance * 0.3) {
    return false; // Too much vertical offset
  }

  // Check that 30pt is below 100pt holes
  const avg100ptY = (left100pt.y + right100pt.y) / 2;
  if (center30pt.y <= avg100ptY) {
    return false; // 30pt should be below 100pt
  }

  // Check that 30pt is roughly centered between 100pt holes
  const centerX = (left100pt.x + right100pt.x) / 2;
  const offsetFromCenter = Math.abs(center30pt.x - centerX);
  if (offsetFromCenter > horizontalDistance * 0.3) {
    return false; // 30pt too far off-center
  }

  return true;
}

